/**
 * Discounts v2 targeting — matches discount elements to product variants via
 * the PRODVIEW hierarchy. Shared by the backend candidate engine and the
 * frontend picker so both agree on what applies.
 *
 * PRODVIEW is a dot-separated hierarchy on `im_product_variants.origin_metadata`,
 * e.g. "PARTS.CIJ.EXCEL SINGLE.170":
 *   segment 0 = category (PARTS / FLUID / EQUIPMENT / …)
 *   segment 1 = product line (CIJ / LCM / …)
 *   segment 2 = family
 *   segment 3 = model group
 */

import { DiscountElementTargetType } from "@erp/shared-types";

export interface ProdViewSegments {
  category: string | null;
  productLine: string | null;
  family: string | null;
  modelGroup: string | null;
}

/**
 * Extract all four PRODVIEW hierarchy segments, uppercased. (The legacy v1
 * `extractProdViewSegments` only extracts category + product line — do not
 * reuse it for Discounts v2 targeting.)
 */
export function extractProdViewSegmentsV2(
  prodView: string | null | undefined,
): ProdViewSegments {
  const segments = (prodView ?? "")
    .split(".")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return {
    category: segments[0]?.toUpperCase() ?? null,
    productLine: segments[1]?.toUpperCase() ?? null,
    family: segments[2]?.toUpperCase() ?? null,
    modelGroup: segments[3]?.toUpperCase() ?? null,
  };
}

export interface DiscountTargetInput {
  targetType: DiscountElementTargetType;
  targetValue: string | null;
}

export interface VariantTargetContext {
  variantId: string;
  /** Raw PRODVIEW string from the variant's origin metadata; null when absent. */
  prodView: string | null;
}

/**
 * Does an element's target match this variant? Unknown hierarchy segments
 * (missing PRODVIEW) fail closed — an element targeting a category never
 * matches a variant whose category is unknown.
 */
export function elementTargetsVariant(
  target: DiscountTargetInput,
  variant: VariantTargetContext,
): boolean {
  switch (target.targetType) {
    case DiscountElementTargetType.ALL_PRODUCTS:
      return true;
    case DiscountElementTargetType.PRODUCT_VARIANT_ID:
      return target.targetValue === variant.variantId;
    case DiscountElementTargetType.PRODUCT_CATEGORY:
    case DiscountElementTargetType.PRODUCT_LINE:
    case DiscountElementTargetType.PRODUCT_FAMILY:
    case DiscountElementTargetType.PRODUCT_GROUP: {
      if (!target.targetValue) return false;
      const segments = extractProdViewSegmentsV2(variant.prodView);
      const segment = {
        [DiscountElementTargetType.PRODUCT_CATEGORY]: segments.category,
        [DiscountElementTargetType.PRODUCT_LINE]: segments.productLine,
        [DiscountElementTargetType.PRODUCT_FAMILY]: segments.family,
        [DiscountElementTargetType.PRODUCT_GROUP]: segments.modelGroup,
      }[target.targetType];
      return segment !== null && segment === target.targetValue.toUpperCase();
    }
    default:
      return false;
  }
}
